import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { EmployeeHeader } from "../components/EmployeeHeader";
import { Skeleton } from "../components/ui/skeleton";
import { CheckCircle2, History as HistoryIcon, Clock } from "lucide-react";

const PRIORITY_TONE = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-sky-100 text-sky-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

function dayLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
}

function timeLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function groupByDay(items) {
  const order = [];
  const map = new Map();
  items.forEach((t) => {
    const label = dayLabel(t.completed_at);
    if (!map.has(label)) {
      map.set(label, []);
      order.push(label);
    }
    map.get(label).push(t);
  });
  return order.map((label) => ({ label, items: map.get(label) }));
}

export default function EmployeeTasks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/me/tasks/history")
      .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    // Viewing History is the natural "seen" trigger now that the header bell
    // is wired to the central notification system instead of task badges.
    apiClient.post("/me/tasks/seen").catch(() => {});
  }, []);

  const groups = groupByDay(items);

  return (
    <>
      <EmployeeHeader title="History" subtitle="Completed tasks · last 7 days" />
      <div className="px-4 pt-4 pb-28 space-y-5">
        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200" data-testid="emp-history-empty">
            <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto grid place-items-center mb-3">
              <HistoryIcon className="w-7 h-7 text-slate-400" strokeWidth={2.5} />
            </div>
            <p className="text-slate-700 font-semibold mb-1">No completed tasks yet</p>
            <p className="text-sm text-slate-500">Tasks you finish in the last 7 days show up here</p>
          </div>
        ) : (
          <div data-testid="emp-history-list">
            {groups.map(({ label, items: dayItems }) => (
              <div key={label} className="mb-5" data-testid={`history-group-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1 mb-2">{label}</h3>
                <div className="space-y-2">
                  {dayItems.map((t) => (
                    <Link key={t.id} to={`/my-tasks/${t.id}`} data-testid={`history-task-${t.id}`}
                      className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-3 active:bg-slate-50">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 truncate">{t.client_name || t.title}</div>
                        {t.items?.length > 0 && (
                          <div className="text-xs text-slate-500 truncate">
                            {t.items.slice(0, 2).join(", ")}{t.item_count > 2 ? "…" : ""}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {t.priority && (
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.medium}`}>
                              {t.priority}
                            </span>
                          )}
                          <span className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {timeLabel(t.completed_at)}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                        Completed
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
